/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React, { MouseEvent } from 'react';
import IconButton from '@mui/material/IconButton';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useAtomValue } from 'jotai';
import { WebMercatorViewport } from '@deck.gl/core';
import { viewStateAtom, type ClickInfo } from '../atoms';
import { PopupContainer } from '../PopupContainer';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { FeatureInfo } from '../../Incident/FeaturePopup';
import { IPAWSAlertInfo } from '../../Ipaws/IPAWSAlertPopup';
import { isTingLayerId } from '../../ting/TingOutagesLayer';

interface LayerInfoPopupProps {
  clickInfo: ClickInfo | null;
  activeObjectIndex: number;
  handlePrevClick: (event: MouseEvent<HTMLButtonElement>) => void;
  handleNextClick: (event: MouseEvent<HTMLButtonElement>) => void;
  onClose: () => void;
}

/**
 * The clicked point's place on screen under the current camera, with the
 * canvas size it is measured in. Recomputed on every camera change, so the
 * popup follows its feature through pans, zooms and fly-tos. Null until deck
 * has measured the canvas, before which nothing can have been clicked, and
 * while the point is not in front of the camera.
 */
function useScreenAnchor(coordinate: ClickInfo['coordinate'] | undefined) {
  const viewState = useAtomValue(viewStateAtom);
  return React.useMemo(() => {
    const { width, height } = viewState;
    if (coordinate == null || width == null || height == null) return null;
    const [longitude, latitude, altitude = 0] = coordinate;
    const [x, y, depth] = new WebMercatorViewport({ ...viewState, width, height }).project([
      longitude,
      latitude,
      altitude,
    ]);
    // Under a steep pitch the perspective divide brings a point behind the
    // camera, or past the horizon, back onto the canvas mirrored, at a depth
    // beyond the far plane. It is not on screen, so there is nothing to
    // anchor to.
    if (depth > 1) return null;
    return { point: { x, y }, bounds: { width, height } };
  }, [viewState, coordinate]);
}

const LayerInfoPopup: React.FC<LayerInfoPopupProps> = ({
  clickInfo,
  activeObjectIndex,
  handlePrevClick,
  handleNextClick,
  onClose,
}) => {
  const anchor = useScreenAnchor(clickInfo?.coordinate);

  if (
    anchor === null ||
    !clickInfo ||
    !clickInfo.objects ||
    !clickInfo.objects.length ||
    activeObjectIndex < 0 ||
    activeObjectIndex >= clickInfo.objects.length
  ) {
    return null;
  }

  function handleLayerName(layerId: string) {
    switch (true) {
      case layerId.includes('ipaws-post'):
        return 'IPAWS-POST';
      case layerId.includes('ipaws'):
        return 'IPAWS Boundary';
      case layerId.includes('nifc-post'):
        return 'NIFC-POST';
      case layerId.includes('nifc'):
        return 'NIFC Boundary';
      case layerId.includes('wow-post'):
        return 'WOW-POST';
      case layerId.includes('wow'):
        return 'WOW Boundary';
      case layerId.includes('canopy-post'):
        return 'Canopy-POST';
      case layerId.includes('population'):
        return 'Population';
      case layerId.includes('residential'):
        return 'Residential';
      case layerId.includes('nonResidential'):
        return 'Non-Residential';
      case layerId.includes('energy'):
        return 'Energy';
      case layerId.includes('transportation'):
        return 'Transportation';
      case layerId.includes('water'):
        return 'Food, Water, and Shelter';
      case layerId.includes('medical'):
        return 'Health & Medical';
      case layerId.includes('communication'):
        return 'Communications';
      // Ting first: its historical id is `weather-ting-events-layer-threats`,
      // which the generic historical-threats case below would otherwise claim.
      case isTingLayerId(layerId):
        return 'Ting Power Outages';
      // The historical threat layers are `weather-<feed>-events-layer-threats`;
      // wow and nifc resolve on their bare cases above, so this labels canopy's.
      case layerId.includes('-events-layer-threats'):
        return 'Weather Threats';
      case layerId.includes('canopy-swath'):
        return 'Tornado Swath';
      case layerId.includes('usa-structures'):
        return 'USA Structures';
      default:
        return layerId;
    }
  }
  const feature = clickInfo.objects[activeObjectIndex].object;
  const layerId = clickInfo.objects[activeObjectIndex].layer.id;
  const features = clickInfo.objects.map(o => o.object);

  return (
    <PopupContainer anchor={anchor.point} bounds={anchor.bounds} onClose={onClose}>
      {/* Inside the popup frame, so a feature whose details cannot be rendered
          shows its error in the popup, with Close still at hand, instead of
          taking the map with it. A new click or a step to another feature
          clears a caught error. */}
      <ErrorBoundary
        region="The feature details"
        resetKey={`${clickInfo.coordinate.join(',')}|${activeObjectIndex}`}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 12 }}>
            {features.length > 1 && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="previous"
                onClick={handlePrevClick}
                disabled={activeObjectIndex === 0}
              >
                <NavigateBeforeIcon />
              </IconButton>
            )}
            <h5 style={{ margin: 0 }}>
              Layer: {handleLayerName(layerId)}{' '}
              {features.length > 1 && `(${activeObjectIndex + 1} / ${features.length})`}
            </h5>
            {features.length > 1 && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="next"
                onClick={handleNextClick}
                disabled={activeObjectIndex === clickInfo.objects.length - 1}
              >
                <NavigateNextIcon />
              </IconButton>
            )}
          </div>
          {layerId.includes('ipaws') && !layerId.includes('ipaws-post') && (
            <IPAWSAlertInfo {...feature.properties} />
          )}

          <FeatureInfo feature={feature} layerId={layerId} />
        </div>
      </ErrorBoundary>
    </PopupContainer>
  );
};

export default LayerInfoPopup;
